import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';

import {
  DataListingRequestStatus,
  GetEmployeesPayload,
  SearchForeignersPayload,
  GetUsersPayload,
  ReassignForeignersOpts,
  Role,
  SelfRegisterOpts,
  User,
  USER_TAGS,
} from '@interfaces';
import { Actions, ofType } from '@ngrx/effects';
import { AppState } from '@state';
import * as usersActions from './users.actions';
import * as usersSelectors from './users.selectors';

@Injectable()
export class UsersFacade {
  private readonly store = inject<Store<AppState>>(Store);
  private readonly actions = inject(Actions);

  public loading$ = this.store.select(usersSelectors.selectLoading);
  public errorMessage$ = this.store.select(usersSelectors.selectErrorMessage);
  public users$ = this.store.select(usersSelectors.selectLoadedUsers);
  public foreignerBasicData$ = this.store.select(usersSelectors.selectForeignerBasicData);
  public employeeBasicData$ = this.store.select(usersSelectors.selectEmployeeBasicData);

  public pagination$ = this.store.select(usersSelectors.selectLoadedPagination);

  public loadingEmployeeTypes$ = this.store.select(usersSelectors.selectLoadingEmployeeTypes);
  public employeeTypes$ = this.store.select(usersSelectors.selectEmployeeTypes);

  public loadingDataListingRequest$ = this.store.select(usersSelectors.selectLoadingDataListingRequest);
  public dataListingRequests$ = this.store.select(usersSelectors.selectDataListingRequests);

  public saveSuccess$ = this.actions.pipe(
    ofType(usersActions.updateUserSuccess, usersActions.createUserSuccess),
    map((result: { updatedUser?: User; createdUser?: User }) => result.updatedUser || result.createdUser)
  );

  public saveError$ = this.actions.pipe(
    ofType(usersActions.updateUserError, usersActions.createUserError),
    map((error: { errorMessage: string }) => error.errorMessage)
  );

  public resendSuccess$ = this.actions.pipe(ofType(usersActions.resendActivationSuccess));
  public removeUserSuccess$ = this.actions.pipe(ofType(usersActions.removeUserSuccess));
  public restoreUserSuccess$ = this.actions.pipe(ofType(usersActions.restoreUserSuccess));
  public selfRegisterSuccess$ = this.actions.pipe(ofType(usersActions.selfRegisterSuccess));

  public reassignInProgress$ = this.store.select(usersSelectors.selectReassignInProgress);
  public reassignForeignersSuccess$ = this.actions.pipe(ofType(usersActions.reassignForeignersSuccess));
  public reassignForeignersError$ = this.actions.pipe(ofType(usersActions.reassignForeignersError));

  public getUsersSuccess$ = this.actions.pipe(ofType(usersActions.getUsersSuccess));
  public getUsersError$ = this.actions.pipe(ofType(usersActions.getUsersError));

  public updateUserSuccess$ = this.actions.pipe(ofType(usersActions.updateUserSuccess));
  public updateUserError$ = this.actions.pipe(ofType(usersActions.updateUserError));

  public modifyUsersTagsSuccess$ = this.actions.pipe(ofType(usersActions.modifyUsersTagsSuccess));
  public modifyUsersTagsError$ = this.actions.pipe(ofType(usersActions.modifyUsersTagsError));

  public getAllRequestsDataListingSuccess$ = this.actions.pipe(ofType(usersActions.getAllRequestsDataListingSuccess));
  public getAllRequestsDataListingError$ = this.actions.pipe(ofType(usersActions.getAllRequestsDataListingError));

  public getDataListingRequestsOfUserSuccess$ = this.actions.pipe(
    ofType(usersActions.getDataListingRequestsOfUserSuccess)
  );
  public getDataListingRequestsOfUserError$ = this.actions.pipe(ofType(usersActions.getDataListingRequestsOfUserError));

  public requestDataListingSuccess$ = this.actions.pipe(ofType(usersActions.requestDataListingSuccess));
  public requestDataListingError$ = this.actions.pipe(ofType(usersActions.requestDataListingError));

  public changeDataListingRequestStatusSuccess$ = this.actions.pipe(
    ofType(usersActions.changeDataListingRequestStatusSuccess)
  );
  public changeDataListingRequestStatusError$ = this.actions.pipe(
    ofType(usersActions.changeDataListingRequestStatusError)
  );

  public getUserConsentsSuccess$ = this.actions.pipe(ofType(usersActions.getUserConsentsSuccess));
  public getUserConsentsError$ = this.actions.pipe(ofType(usersActions.getUserConsentsError));

  public searchForeigners(payload: SearchForeignersPayload): void {
    this.store.dispatch(usersActions.searchForeigners({ payload }));
  }

  public getEmployees(payload: GetEmployeesPayload): void {
    this.store.dispatch(usersActions.getEmployees({ payload }));
  }

  public getCompanyEmployees(payload: GetUsersPayload): void {
    this.store.dispatch(usersActions.getCompanyEmployees({ payload }));
  }

  public getUsers(payload: GetUsersPayload): void {
    this.store.dispatch(usersActions.getUsers({ payload }));
  }

  public createForeigner(payload: Partial<User>): void {
    this.store.dispatch(usersActions.createForeigner({ payload }));
  }

  public createEmployee(payload: Partial<User>): void {
    this.store.dispatch(usersActions.createEmployee({ payload }));
  }

  public updateForeigner(payload: Partial<User>, userId: string): void {
    this.store.dispatch(usersActions.updateForeigner({ payload, userId }));
  }

  public updateEmployee(payload: Partial<User>, userId: string): void {
    this.store.dispatch(usersActions.updateEmployee({ payload, userId }));
  }

  public removeForeigner(userId: string): void {
    this.store.dispatch(usersActions.removeForeigner({ userId }));
  }

  public restoreForeigner(userId: string): void {
    this.store.dispatch(usersActions.restoreForeigner({ foreignerId: userId }));
  }

  public restoreUser(userId: string): void {
    this.store.dispatch(usersActions.restoreUser({ userId }));
  }

  public removeEmployee(userId: string): void {
    this.store.dispatch(usersActions.removeEmployee({ userId }));
  }

  public getEmployeeTypes(): void {
    this.store.dispatch(usersActions.getEmployeeTypes());
  }

  public getEmployeeTypes$(): Observable<Role[] | null> {
    return this.store.select(usersSelectors.selectEmployeeTypes).pipe(
      tap(employeeTypes => {
        if (employeeTypes === null) {
          this.getEmployeeTypes();
        }
      }),
      filter(employeeTypes => employeeTypes !== null)
    );
  }

  public getAllRoles(): void {
    this.store.dispatch(usersActions.getAllRoles());
  }

  public getAllRoles$(): Observable<Role[] | null> {
    return this.store.select(usersSelectors.selectAllRoles).pipe(
      tap(allRoles => {
        if (allRoles === null) {
          this.getAllRoles();
        }
      }),
      filter(employeeTypes => employeeTypes !== null)
    );
  }

  public resendEmployeeActivation(email: string, userId: string): void {
    this.store.dispatch(usersActions.resendEmployeeActivation({ email, userId }));
  }

  public resendForeignerActivation(email: string, userId: string): void {
    this.store.dispatch(usersActions.resendForeignerActivation({ email, userId }));
  }

  public selfRegister(opts: SelfRegisterOpts): void {
    this.store.dispatch(usersActions.selfRegister({ opts }));
  }
  public reassignForeigners(opts: ReassignForeignersOpts): void {
    this.store.dispatch(usersActions.reassignForeigners({ opts }));
  }

  // Tags
  public modifyUsersTags(payload: { userIds: string[]; tags: Partial<Record<USER_TAGS, boolean>> }): void {
    this.store.dispatch(usersActions.modifyUsersTags({ payload }));
  }

  // User Settings - Requests Data Listing
  public getAllRequestsDataListing(): void {
    this.store.dispatch(usersActions.getAllRequestsDataListing());
  }

  public getDataListingRequestsOfUser(payload: { userId: string }): void {
    this.store.dispatch(usersActions.getDataListingRequestsOfUser({ payload }));
  }

  public requestDataListing(): void {
    this.store.dispatch(usersActions.requestDataListing());
  }

  public changeDataListingRequestStatus(payload: {
    dataListingRequestId: string;
    status: DataListingRequestStatus;
  }): void {
    this.store.dispatch(usersActions.changeDataListingRequestStatus({ payload }));
  }

  // User Settings - Consents
  public getUserConsents(): void {
    this.store.dispatch(usersActions.getUserConsents());
  }

  public getForeignerBasicData(foreignerId: string): void {
    this.store.dispatch(usersActions.getForeignerBasicData({ foreignerId }));
  }

  public getForeignerBasicData$(foreignerId: string): Observable<Partial<User> | null> {
    return this.store.select(usersSelectors.selectForeignerBasicData).pipe(
      tap(foreigner => {
        if (!foreigner || foreigner.id !== foreignerId) {
          this.getForeignerBasicData(foreignerId);
        }
      }),
      filter(foreigner => {
        return !(foreigner === null || foreigner.id !== foreignerId);
      })
    );
  }

  public resetLoadedForeignerBasicData(): void {
    this.store.dispatch(usersActions.resetLoadedForeignerBasicData());
  }

  public getEmployeeBasicData(employeeId: string): void {
    this.store.dispatch(usersActions.getEmployeeBasicData({ employeeId }));
  }

  public getEmployeeBasicData$(employeeId: string): Observable<Partial<User> | null> {
    return this.store.select(usersSelectors.selectEmployeeBasicData).pipe(
      tap(employee => {
        if (!employee || employee.id !== employeeId) {
          this.getEmployeeBasicData(employeeId);
        }
      }),
      filter(employee => {
        return !(employee === null || employee.id !== employeeId);
      })
    );
  }

  public resetLoadedEmployeeBasicData(): void {
    this.store.dispatch(usersActions.resetLoadedEmployeeBasicData());
  }
}
